import React, { useState } from 'react';
import { Box, Dialog, DialogContent } from '@mui/material';
import ContactMail from '@mui/icons-material/ContactMail';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import { READ_EMPLOYEE_CONTACT_INFORMATION, UPDATE_EMPLOYEE_CONTACT_INFORMATION } from 'domain/shared/employee';
import { ShowSection } from 'framework/ui/layout';
import { useFormContext, DEFAULT_ERROR_MESSAGE, isEmptyFormErrors } from 'framework/form';
import SectionField from './SectionField';
import ContactInfoItem from './ContactInfoItem';
import { ContactInfoContentProps } from './types';
import EditDialogActions from '../createupdate/EditDialogActions';
import ContactInfoForm from '../createupdate/ContactInfoForm';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import EditFormShowButton from 'domain/people/employee/shared/button/showEdit/EditFormShowButton';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';
import PrepopulateButton from 'domain/people/employee/prepopulation/PrepopulateButton';
import ContactInfoPrepopulateModal from '../createupdate/ContactInfoPrepopulateModal';
import { InlineAddressField } from 'framework/ui/field';

const ContactInfoContent: React.FC<ContactInfoContentProps> = () => {
  const { data: employee, setData } = useShowContext();
  const { isGranted } = useAuthorization();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isPrepopulateModalOpen, setIsPrepopulateModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { addMessage } = useMessageService();
  const { forms = [] } = usePrepopulationContext();

  const { submitForm, validateForm } = useFormContext();

  if (!isGranted(READ_EMPLOYEE_CONTACT_INFORMATION)) {
    return null;
  }

  const personalPhoneNumber = employee?.getValueBySource('extendedInformation.personalPhoneNumber');
  const personalEmail = employee?.getValueBySource('extendedInformation.personalEmail');
  const additionalContactInfo = employee?.getValueBySource('extendedInformation.additionalContactInfo');
  const homeAddress = employee?.getValueBySource('extendedInformation.homeAddress');

  const hasPrepopulationForms = forms.length > 0;
  const shouldShowPrepopulateButton = hasPrepopulationForms && !homeAddress;

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const errors = validateForm?.();

      if (errors && !isEmptyFormErrors(errors)) {
        setIsSaving(false);
        return;
      }

      await submitForm?.();
      handleCloseEditModal();
    } catch (error) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    } finally {
      setIsSaving(false);
    }
  };

  const renderActions = () => {
    if (!isGranted([UPDATE_EMPLOYEE_CONTACT_INFORMATION])) {
      return null;
    }

    return (
      <Box display="flex" alignItems="center" gap="10px">
        {shouldShowPrepopulateButton && (
          <PrepopulateButton
            scrollToPrepopulatedInputs={() => setIsPrepopulateModalOpen(true)}
            sx={{ mt: 0.5, mr: 1 }}
          />
        )}
        <EditFormShowButton
          onClick={() => setOpenEditModal(true)}
          requiredPermission={[UPDATE_EMPLOYEE_CONTACT_INFORMATION]}
        />
      </Box>
    );
  };

  return (
    <>
      <ShowSection
        sx={{
          position: 'relative',
          '.show-title-container': {
            marginBottom: '20px',
          },
        }}
        title={
          <ContactMail
            sx={{
              color: 'text.primary',
            }}
          />
        }
        actions={
          <Box
            sx={{
              position: 'absolute',
              right: '-15px',
              top: '-4px',
              button: {
                m: 0,
              },
            }}
          >
            {renderActions()}
          </Box>
        }
        disableDefaultPadding
      >
        <Box>
          <Box display="grid" gridTemplateColumns="minmax(150px, 0.33fr) 1fr" gap={3}>
            <SectionField label="Personal email address">{personalEmail}</SectionField>
            <SectionField label="Registration address">
              <InlineAddressField source="extendedInformation.registrationAddress" label="" />
            </SectionField>
            <SectionField label="Personal phone number">{personalPhoneNumber}</SectionField>
            <SectionField label="Home address">
              <InlineAddressField source="extendedInformation.homeAddress" label="" />
            </SectionField>
            {additionalContactInfo?.length > 0 && (
              <SectionField label="Other contact information">
                <Box display="flex" flexDirection="column" gap={1}>
                  {additionalContactInfo.map((contact: { type: string; value: string }) => (
                    <ContactInfoItem key={contact.value} contact={contact} />
                  ))}
                </Box>
              </SectionField>
            )}
          </Box>
        </Box>
      </ShowSection>

      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            overflow: 'visible',
          },
        }}
      >
        <DialogContent sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ContactInfoForm />
        </DialogContent>
        <EditDialogActions onClose={handleCloseEditModal} onSave={handleSave} isSaving={isSaving} />
      </Dialog>

      <ContactInfoPrepopulateModal
        isOpen={isPrepopulateModalOpen}
        onClose={() => setIsPrepopulateModalOpen(false)}
        onSave={(updatedData?: any) => {
          if (setData && updatedData) {
            setData(updatedData);
          }
          setIsPrepopulateModalOpen(false);
        }}
      />
    </>
  );
};

export default ContactInfoContent;
